import React, { memo } from 'react';
import { 
  Home, Package, 
  ShoppingBag, User,
  LogOut, X, Crown, Bell,
  LifeBuoy, Gift, ShoppingCart, Layers, BookOpen
} from 'lucide-react';
import { UserProfile, Product } from '../types';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  activeTab: string;
  onNavigate: (tab: string, filter?: any) => void;
  onLogout: () => void;
  user: UserProfile | null;
  cartCount: number;
  translations?: any;
  products?: Product[];
}

const SidebarLink = memo(({ item, active, onNavigate, onClose }: any) => {
  const Icon = item.icon;
  return (
    <button
      onClick={() => {
        onNavigate(item.id);
        onClose();
      }}
      className={`w-full flex items-center gap-3.5 px-4 py-3 rounded-2xl transition-all duration-200 group relative overflow-hidden
        ${active
          ? 'bg-black text-white shadow-xl scale-[1.02]'
          : 'text-slate-600 hover:bg-slate-50'
        }`}
    >
      <div className={`transition-all duration-300 p-2 rounded-xl group-active:scale-90
        ${active ? 'bg-white/10 text-white' : `bg-white shadow-sm border border-slate-100 ${item.color}`}`}>
        <Icon size={18} strokeWidth={2.5} />
      </div>
      <span className="text-[11px] font-black uppercase tracking-wider flex-1 text-left italic">{item.label}</span>

      {active && (
        <div className="absolute right-2 w-1.5 h-1.5 rounded-full bg-orange-500 shadow-[0_0_10px_#f97316]" />
      )}
    </button>
  );
});

export const Sidebar: React.FC<SidebarProps> = memo(({
  isOpen, onClose, activeTab, onNavigate, onLogout, user
}) => {
  const menuItems = [
    { id: 'home', icon: Home, label: 'HOME', color: 'text-blue-500' },
    { id: 'ledger', icon: BookOpen, label: 'LEDGER', color: 'text-emerald-500' },
    { id: 'partners', icon: BookOpen, label: 'OUR BRANDS', color: 'text-orange-500' },
    { id: 'notifications', icon: Bell, label: 'NOTIFICATIONS', color: 'text-amber-500' },
    { id: 'offers', icon: Gift, label: 'OFFERS', color: 'text-rose-500' },
    { id: 'profile', icon: User, label: 'PROFILE', color: 'text-indigo-500' },
    { id: 'rcm_products', icon: Crown, label: 'RCM STORE', color: 'text-orange-500' },
    { id: 'products', icon: Package, label: 'HARDWARE', color: 'text-emerald-500' },
    { id: 'orders', icon: ShoppingBag, label: 'ORDERS', color: 'text-violet-500' },
    { id: 'cart', icon: ShoppingCart, label: 'CART', color: 'text-cyan-500' },
    { id: 'support', icon: LifeBuoy, label: 'SUPPORT', color: 'text-sky-500' },
  ];

  const SidebarContent = (
    <div className="flex flex-col h-full bg-white overflow-hidden">
      <div className="p-8 flex flex-col gap-6 shrink-0">
        <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-orange-500 rounded-2xl flex items-center justify-center shadow-lg rotate-3 shadow-orange-500/30 border-2 border-white">
                <span className="text-white font-black italic text-xl drop-shadow-md">R</span>
            </div>
            <h1 className="text-2xl font-black text-black tracking-tighter uppercase italic leading-none">
              RCM<br/><span className="text-orange-500">DEALER</span>
            </h1>
        </div>

        <div className="p-5 bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-[24px] space-y-1 border border-slate-100 relative overflow-hidden group">
            <div className="absolute -right-4 -top-4 w-16 h-16 bg-white/40 rounded-full blur-2xl group-hover:bg-orange-200/40 transition-colors" />
            <p className="text-black font-black text-xs uppercase truncate italic relative z-10">{user?.shop_name || "RCM Dealer"}</p>
            <div className="flex items-center gap-2 relative z-10">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                <p className="text-slate-400 text-[8px] font-bold uppercase tracking-[0.2em]">Live Session</p>
            </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 space-y-8 no-scrollbar py-4">
        <div className="space-y-2">
            <p className="text-[8px] font-black text-slate-400 uppercase tracking-[0.3em] px-2 mb-2 flex items-center gap-2">
                <Layers size={10} /> Main Hub
            </p>
            {menuItems.map((item) => (
                <SidebarLink key={item.id} item={item} active={activeTab === item.id} onNavigate={onNavigate} onClose={onClose} />
            ))}
        </div>
      </div>

      <div className="p-8 border-t border-slate-50 shrink-0">
        <button
            onClick={onLogout}
            className="w-full h-14 rounded-2xl text-red-500 font-black uppercase tracking-widest text-[10px] flex items-center justify-center gap-3 bg-red-50 border border-red-100 active:scale-95 transition-all italic hover:bg-red-100/50"
        >
          <LogOut size={16} strokeWidth={3} /> Logout Account
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className="hidden lg:block w-72 h-screen fixed left-0 top-0 z-50 border-r border-slate-100 shadow-sm">{SidebarContent}</div>
      {isOpen && (
          <div key="sidebar-overlay" className="lg:hidden fixed inset-0 z-[60]">
            <div
                onClick={onClose}
                className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            />
            <div
                className="absolute left-0 top-0 bottom-0 w-80 shadow-2xl overflow-hidden rounded-r-[40px] bg-white border-r border-slate-100"
            >
              {SidebarContent}
              <button
                onClick={onClose}
                className="absolute top-8 -right-4 h-12 w-12 bg-white rounded-full flex items-center justify-center text-black shadow-2xl border border-slate-100 active:scale-90 transition-all z-50"
              >
                <X size={20} strokeWidth={3} />
              </button>
            </div>
          </div>
        )}
    </>
  );
});
